/** The itinerary record's own inputs (plan §5 "Cards", D3, O14), registered under the records
 *  core's names (`coreWidget`) — the core's built-ins cover the rest (text, textarea, number,
 *  integer, enum, boolean, rich_text):
 *
 *  - `currency` — the global currencies list;
 *  - `tour` — the App button's tour: one of the operator's tours, neither deleted nor bespoke
 *    (catalog picker `cta_tours`);
 *  - `line_set` — one of the operator's line sets (catalog picker `line_sets`);
 *  - `tags` — the four id sets, each from its own catalog picker: Regions (`regions`), Filters
 *    (`trip_types`: the operator's or its franchise parent's), Catalog search tags (`tags`) and
 *    Access tags (`atags`);
 *  - `months` — twelve compact toggles, "Any month" when none (`MonthsWidget`);
 *  - `image` — the route map (purpose `route_map`) and a day's image (`day_image`): picking a
 *    file uploads it at once (`POST /media/staged/`, the itinerary's operator) and the draft
 *    holds the Image id the upload answers (D3); the save attaches it. The stored image is
 *    edit_context's Image id drawn from the detail's CDN keys.
 *  - `days` — the day list: no input of its own (the Days card's outline edits it); its words
 *    in the ledger — the days by name in their order, a new one "New day".
 *  - `day_span` — a day's span (the catalog's `integer`): a whole number of days from 1 to
 *    32767, which the catalog does not say — a 0, a negative or a blank is refused at the input
 *    in the changeset's words (`DAY_SPAN_REFUSED`), so it never reaches the draft or the
 *    preview.
 *
 *  Every option shows its pk, and the operator's own rows its tenant code (the
 *  remote-autocomplete rule). An itinerary without an operator has nothing to pick from (its
 *  Edit is blocked anyway). Create the set once per itinerary (`useItineraryWidgets`): a new
 *  definition per render would remount the inputs; the stored images are read at render time,
 *  so a save's re-seeded image shows without remounting. */
import { useMemo, useRef } from 'react';

import { catalogPicker, type CatalogPickerKind } from '@/api/catalogPickers';
import type { ItineraryDetail } from '@/api/itineraries';
import { ITINERARY_DAY_SPAN_MAX } from '@/api/itineraryRecord';
import { listCurrencies } from '@/api/pickers';
import { stagedImageUploader } from '@/api/stagedImages';
import { currencyLabel } from '@/features/activities/record/widgets';
import { parseRecordKey } from '@/features/records/draft/changeSet';
import type { RecordField, RecordOptionRef, RecordWidget, RecordWidgetProps } from '@/features/records/editor/types';
import { ImageWidget, type StoredImage } from '@/features/records/fields/widgets/ImageWidget';
import { createRecordPicker, createTagsWidget } from '@/features/records/fields/widgets/pickers';
import { boundedIntegerWidget } from '@/features/records/fields/widgets/TextWidgets';

import { ITINERARY_DAY_ENTITY, ITINERARY_DAY_REGISTRY } from './registry';
import { MonthsWidget } from './MonthsWidget';

/** The id sets, by field: the catalog picker each picks from, its group and its search's words. */
const SETS: Readonly<Record<string, { kind: CatalogPickerKind; group: string; placeholder: string; own: boolean }>> = {
  regions: { kind: 'regions', group: 'Regions', placeholder: 'Add a region', own: true },
  trip_types: { kind: 'trip_types', group: 'Filters', placeholder: 'Add a filter', own: false },
  tags: { kind: 'tags', group: 'Catalog search tags', placeholder: 'Add a search tag', own: true },
  atag: { kind: 'atags', group: 'Access tags', placeholder: 'Add an access tag', own: false },
};

type Tenant = ItineraryDetail['tenant'];

async function pickCatalog(
  tenant: Tenant,
  kind: CatalogPickerKind,
  q: string,
  signal: AbortSignal,
  group: string,
  ownRows: boolean,
): Promise<RecordOptionRef[]> {
  if (!tenant) return [];
  const { results } = await catalogPicker(tenant.id, kind, { q, signal });
  return results.map((o) => ({
    id: o.id,
    label: o.name ?? `#${o.id}`,
    group,
    // The operator's own rows name it; trip types and access tags may be its franchise
    // parent's, which the picker does not say.
    ...(ownRows ? { tenant: tenant.code } : {}),
  }));
}

/** The stored images the image inputs show: the route map's, and a day's by its id — read at
 *  render time. */
export interface ItineraryStoredImages {
  routeMap: () => StoredImage | null;
  dayImage: (dayId: number) => StoredImage | null;
}

/** The words of an image field without an image. */
export const NO_ROUTE_MAP = 'No route map';
export const NO_DAY_IMAGE = 'No image';

/** The widget name a day's span takes (`itineraryOwnInputs`). */
export const DAY_SPAN_WIDGET = 'day_span';

/** A day's span refused, in the changeset's words (`itinerary_changeset/values.py`). */
export const DAY_SPAN_REFUSED = `${ITINERARY_DAY_REGISTRY.day_span.label}: must be a whole number of days from 1 to ${ITINERARY_DAY_SPAN_MAX}.`;

/** A day's span: the integer input, refusing what the changeset would. */
export const DaySpanWidget = boundedIntegerWidget((days) =>
  days !== null && days >= 1 && days <= ITINERARY_DAY_SPAN_MAX ? null : DAY_SPAN_REFUSED,
);

/** The day list's words: "Bangkok arrival, Ayutthaya, New day" — a day by its stored title
 *  (`titleOf`), "Untitled day" without one. The Days card is its input; the ledger reads it. */
export function daysWidget(titleOf: (dayId: number) => string | null | undefined): RecordWidget {
  const DaysList = () => null;
  return Object.assign(DaysList, {
    formatChange: (value: unknown) =>
      (Array.isArray(value) ? (value as unknown[]) : [])
        .filter((id): id is number => typeof id === 'number')
        .map((id) => (id < 0 ? 'New day' : titleOf(id)?.trim() || 'Untitled day'))
        .join(', '),
  });
}

/** The itinerary's widgets, bound to its operator. */
export function itineraryWidgets(
  tenant: Tenant,
  images: () => ItineraryStoredImages,
  dayTitle: (dayId: number) => string | null | undefined = () => null,
): Record<string, RecordWidget> {
  const uploadRouteMap = tenant ? stagedImageUploader('route_map', tenant.id) : null;
  const uploadDayImage = tenant ? stagedImageUploader('day_image', tenant.id) : null;
  function ItineraryImageWidget(p: RecordWidgetProps) {
    const { entity, id } = parseRecordKey(p.rec);
    const day = entity === ITINERARY_DAY_ENTITY;
    const upload = day ? uploadDayImage : uploadRouteMap;
    if (!upload) return null;
    const stored = day ? images().dayImage(id) : images().routeMap();
    return (
      <ImageWidget {...p} stored={stored} staging={{ kind: 'uploaded', upload }} emptyText={day ? NO_DAY_IMAGE : NO_ROUTE_MAP} />
    );
  }
  return {
    currency: createRecordPicker({
      load: async (_f, _r, q) =>
        (await listCurrencies(q.trim() || undefined)).results.map((c) => ({
          id: c.id,
          label: currencyLabel(c),
          extra: [c.name],
        })),
      fetchOnEmpty: true,
      placeholder: 'No currency',
    }),
    tour: createRecordPicker({
      load: (_f, _r, q, signal) => pickCatalog(tenant, 'cta_tours', q, signal, 'Tours', true),
      placeholder: 'No tour',
    }),
    line_set: createRecordPicker({
      load: (_f, _r, q, signal) => pickCatalog(tenant, 'line_sets', q, signal, 'Line sets', true),
      fetchOnEmpty: true,
      placeholder: 'No line set',
    }),
    tags: createTagsWidget({
      load: (field: RecordField, _r, q, signal) => {
        const set = SETS[field.key];
        return set ? pickCatalog(tenant, set.kind, q, signal, set.group, set.own) : Promise.resolve([]);
      },
      placeholder: (field) => SETS[field.key]?.placeholder ?? 'Add a tag',
      reserveAddRow: true,
    }),
    months: MonthsWidget,
    image: ItineraryImageWidget,
    days: daysWidget(dayTitle),
    [DAY_SPAN_WIDGET]: DaySpanWidget,
  };
}

/** The detail's CDN keys as a stored image with its Image id (edit_context's). */
export function storedImageOf(id: unknown, chunks: { p: string; s: string } | null | undefined): StoredImage | null {
  if (typeof id !== 'number') return null;
  return { id, url: null, p: chunks?.p ?? null, s: chunks?.s ?? null, name: chunks?.s ?? null };
}

/** The widgets of one itinerary — stable while its operator is. `images` and the days' titles
 *  are read when an input or a ledger row renders. */
export function useItineraryWidgets(
  detail: Pick<ItineraryDetail, 'tenant' | 'days'>,
  images: ItineraryStoredImages,
): Record<string, RecordWidget> {
  const latest = useRef({ images, days: detail.days });
  latest.current = { images, days: detail.days };
  const { tenant } = detail;
  const tenantKey = tenant ? `${tenant.id}:${tenant.code}` : '';
  return useMemo(
    () =>
      itineraryWidgets(
        tenant,
        () => latest.current.images,
        (dayId) => latest.current.days.find((d) => d.id === dayId)?.title,
      ),
    // The pickers and the uploads depend on the operator only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantKey],
  );
}
